import propEq from 'ramda/src/propEq'
import last from 'ramda/src/last'
import findIndex from 'ramda/src/findIndex'
import isEmpty from 'ramda/src/isEmpty'
import head from 'ramda/src/head'
import tail from 'ramda/src/tail'
import clone from 'ramda/src/clone'
import conversationModule from '@/store/modules/conversation.module'
import { CONVERSATION_TYPES } from '@/store/constants'
import { ACTION_TYPES, INPUT_TYPES, STATE_ID, STATE_TYPES } from '@/utils/bot-json.constants'

const NODE_TYPES = {
  OUTPUT: 'output',
  REMOTE: 'remote',
  CONDITION: 'condition',
  FIND_INTENT: 'findIntent',
  // PRO-367: same string as ANSWER_END.CONTINUE_OR_FIND_INTENT so resolveAnswerEndValue() maps it 1:1
  CONTINUE_OR_FIND_INTENT: 'continueOrFindIntent',
  IDLE: 'idle',
  EMPTY: 'empty',
  CONTINUE_TEMPLATE: 'continue_template',
  CONTINUE_CONDITION: 'continue_condition',
  CONTINUE_ANSWER: 'continue_answer',
  SAVE_CONTEXT: 'saveContext',
  VALIDATION: 'validation',
  CHAT_GPT: 'chatGPT'
}

const EDGE_TRANSITION_TYPES = {
  NONE: 'none',
  USER_INPUT: 'userInput',
  QUICK_REPLY: 'quickReply',
  POSTBACK: 'postback',
  CONDITION: 'condition'
}

class StateTreeGenerator {

  constructor(params = {}) {
    if (params.conditionMode) {
      this.conditionMode = true
      this.conditionNode = params.conditionNode
    }

    if (params.postbackMode) this.postbackMode = true
    if (params.templates) this.templates = params.templates
  }

  generate(conversationJsons, conversationType, conversationId, initialStateId = STATE_ID.ROOT) {
    this.templates = conversationJsons.templates
    const botJson = this._getConversationJson(conversationJsons, conversationType, conversationId)
    return this._processState(botJson, initialStateId, null)
  }

  _getConversationJson(conversationJsons, conversationType, conversationId) {
    if (conversationType === CONVERSATION_TYPES.ANSWERS) return conversationJsons.answers[conversationId]
    if (conversationType === CONVERSATION_TYPES.EVENTS) return conversationJsons.events[conversationId]
    if (conversationType === CONVERSATION_TYPES.TEMPLATES) return conversationJsons.templates[conversationId]

    throw new Error('invalid conversation type: ' + conversationType)
  }

  _processState(botJson, stateId, parentEdge, isTemplate = false, inputType = INPUT_TYPES.NONE) {
    if(this.conditionMode && stateId === this.conditionNode.conditionEndStateId) {
      return this._createContinueConditionNode(parentEdge, this.conditionNode, isTemplate)
    }

    if (stateId?.startsWith('ref:answer:')) {
      const continueAnswerNode = this._createContinueAnswerNode(stateId.replace('ref:answer:', ''), parentEdge, isTemplate, this.conditionMode, this.conditionNode, this.postbackMode, !!parentEdge?.transition.payload)
      continueAnswerNode.stateId = this.postbackMode ? parentEdge?.transition.postback : stateId
      return continueAnswerNode
    }

    const state = this._getStateFromConversationJson(botJson, stateId, inputType)
    if(!state ||
      state.type === STATE_TYPES.EMPTY ||
      (stateId === STATE_ID.REF_GLOBAL_IDLE && [EDGE_TRANSITION_TYPES.QUICK_REPLY, EDGE_TRANSITION_TYPES.POSTBACK].includes(parentEdge?.transition.type))
    ) {
      return this._createEmptyNode(stateId, parentEdge, isTemplate)
    }

    switch(state.type) {
      case STATE_TYPES.OUTPUT:
        return this._processStateWithActions(botJson, stateId, parentEdge)
      case STATE_TYPES.CONDITION:
        return this._processConditionState(botJson, stateId, parentEdge)
      case STATE_TYPES.TEMPLATE_REF:
        return this._processTemplateRefState(botJson, stateId, parentEdge)
      case STATE_TYPES.SAVE_CONTEXT:
        return this._processSaveContextState(botJson, stateId, parentEdge)
      case STATE_TYPES.REMOTE:
        if (stateId === STATE_ID.REF_GLOBAL_FIND_INTENT) {
          return this._createFindIntentNode(parentEdge, isTemplate, this.conditionMode, this.conditionNode, this.postbackMode, !!parentEdge?.transition.payload)
        }

        return this._processStateWithActions(botJson, stateId, parentEdge)
      case STATE_TYPES.CALL_OPERATOR:
      case STATE_TYPES.IDLE:
        return this._createIdleNode(parentEdge, isTemplate, this.conditionMode, this.conditionNode, this.postbackMode, !!parentEdge?.transition.payload)
      case STATE_TYPES.CONDITION_END:
        // HACK - we'll only get here in case we are creating a quick reply subtree when
        // the quick reply is inside a condition, ends with Continue and gets deleted.
        // this tree is not used to generate preview items so it's safe to pass a null to the node
        return this._createContinueConditionNode(parentEdge, null, isTemplate)
      default:
        throw new Error('unknown state type ' + state.type)
    }
  }

  _processStateWithActions(botJson, stateId, parentEdge) {
    const state = this._getStateFromConversationJson(botJson, stateId)
    if(isEmpty(state.actions)) return this._createEmptyNode(stateId, parentEdge)

    // process actions
    const nodes = this._processStateActions(botJson, stateId, parentEdge)

    // process transitions
    const lastNode = last(nodes)
    lastNode.children.push(...this._processTransitions(lastNode, state.transitions, botJson, stateId, false))

    return head(nodes)
  }

  _processConditionRefState(botJson, stateId, parentEdge) {
    const state = botJson.states[stateId]
    const conditionId = state.fromTemplate
    const conditionStateId = state.templateState
    const conditionState = this._getTemplateJson(conditionId).states[conditionStateId]
    const node = {
      type: NODE_TYPES.CONDITION,
      isTemplate: true,
      parent: parentEdge,
      children: [],
      cases: [],
      stateId: stateId,
      conditionEndStateId: conversationModule.getConditionEndStateRefId(conditionState.conditionEndState, state.postbackSuffix, state.fromTemplate.replace('ref:template:', ''))
    }
    return this._processConditionNode(botJson, node)
  }

  _processConditionState(botJson, stateId, parentEdge) {
    const node = {
      type: NODE_TYPES.CONDITION,
      isTemplate: false,
      parent: parentEdge,
      children: [],
      cases: [],
      stateId: stateId,
      conditionEndStateId: botJson.states[stateId].conditionEndState
    }
    return this._processConditionNode(botJson, node)
  }

  _processConditionNode(botJson, node) {
    const stateId = node.stateId
    const conditionState = botJson.states[stateId]
    const conditionEndState = botJson.states[node.conditionEndStateId]
    const isTemplate = node.isTemplate

    // process cases
    const caseTransitions = isTemplate ? conditionState.templateTransitions : conditionState.transitions
    for(const caseTransition of caseTransitions) {
      const caseGenerator = new StateTreeGenerator({
        conditionMode: true, conditionNode: node, templates: this.templates, postbackMode: this.postbackMode
      })
      const caseTree = caseGenerator._processState(botJson, caseTransition.state, null, isTemplate, caseTransition.condition.input)
      caseTree.parent = this._createEdge(node, { type: EDGE_TRANSITION_TYPES.CONDITION })

      node.cases.push({
        node: caseTree,
        condition: clone(caseTransition.condition)
      })
    }

    // process transitions after condition end
    if(conditionEndState) {
      const answerEndIsTemplate = isTemplate && !isEmpty(conditionEndState.templateTransitions)
      const conditionEndStateTransitions = answerEndIsTemplate ? conditionEndState.templateTransitions : conditionEndState.transitions
      node.children.push(...this._processTransitions(node, conditionEndStateTransitions, botJson, stateId, answerEndIsTemplate))
    }

    return node
  }

  _processTemplateRefState(botJson, stateId, parentEdge) {
    const state = this._getStateFromConversationJson(botJson, stateId)
    const templateJson = this._getTemplateJson(state.fromTemplate)
    const root = templateJson.states[state.templateState]
    const nodes = []
    if (root.type === STATE_TYPES.SAVE_CONTEXT) {
      nodes.push(this._processTemplateRefSaveContextState(stateId, parentEdge))
    } else if (root.type === STATE_TYPES.CONDITION) {
      nodes.push(this._processConditionRefState(botJson, stateId, parentEdge))
    } else if (root.type === STATE_TYPES.REMOTE) {
      const rootActionId = root.actions[0]
      if ([ACTION_TYPES.CHAT_GPT_GENERAL, ACTION_TYPES.CHAT_GPT_CUSTOM].includes(templateJson.actions[rootActionId].type)) {
        nodes.push(this._processChatGPTAction(stateId, rootActionId, parentEdge, true))
      } else {
        nodes.push(this._processRemoteAction(stateId, rootActionId, parentEdge, true))
      }
    } else if (root.type === STATE_TYPES.OUTPUT) {
      nodes.push(...this._processStateActions(botJson, stateId, parentEdge, true))
    } else {
      console.error(`Cannot process stateId: ${stateId} with template root type: ${root.type}`)
    }
    const lastNode = last(nodes)

    // process template transitions
    if(!isEmpty(state.templateTransitions) && root.type !== STATE_TYPES.CONDITION) {
      lastNode.children.push(...this._processTransitions(lastNode, state.templateTransitions, botJson, stateId, true))
    }
    // process transitions
    if(!isEmpty(state.transitions)) {
      lastNode.children.push(...this._processTransitions(lastNode, state.transitions, botJson, stateId, false))
    }

    return head(nodes)
  }

  _processTemplateRefSaveContextState(stateId, parentEdge) {
    return {
      type: NODE_TYPES.SAVE_CONTEXT,
      isTemplate: true,
      parent: parentEdge,
      children: [],
      stateId: stateId
    }
  }

  _processSaveContextState(botJson, stateId, parentEdge) {
    const state = this._getStateFromConversationJson(botJson, stateId)
    const node = {
      type: NODE_TYPES.SAVE_CONTEXT,
      isTemplate: false,
      parent: parentEdge,
      children: [],
      stateId: stateId
    }

    if(!isEmpty(state.transitions)) {
      node.children.push(...this._processTransitions(node, state.transitions, botJson, stateId, false))
    }

    return node
  }

  _processStateActions(botJson, stateId, parentEdge, isTemplate = false) {
    const actions = this._getStateActions(botJson, stateId)
    const nodes = []

    nodes.push(this._processAction(botJson, stateId, head(actions), parentEdge, isTemplate))
    for(const actionId of tail(actions)) {
      const prevNode = last(nodes)
      const transition = {
        type: EDGE_TRANSITION_TYPES.NONE
      }
      const node = this._processAction(botJson, stateId, actionId, this._createEdge(prevNode, transition), isTemplate)
      nodes.push(node)
      prevNode.children.push(this._createEdge(node, transition))
    }

    return nodes
  }

  _processAction(botJson, stateId, actionId, parentEdge, isTemplate = false) {
    const action = this._getActionFromConversationJson(botJson, stateId, actionId, isTemplate)
    switch(action.type) {
      case ACTION_TYPES.OUTPUT:
        return this._processOutputAction(botJson, stateId, actionId, parentEdge, isTemplate)
      case ACTION_TYPES.REMOTE:
        return this._processRemoteAction(stateId, actionId, parentEdge, isTemplate)
      case ACTION_TYPES.CHAT_GPT_GENERAL:
      case ACTION_TYPES.CHAT_GPT_CUSTOM:
        return this._processChatGPTAction(stateId, actionId, parentEdge, isTemplate)
      case ACTION_TYPES.VALIDATION:
        return this._processValidationAction(stateId, actionId, parentEdge, isTemplate)
      default:
        throw new Error('unknown action type: ' + action.type)
    }
  }

  _processOutputAction(botJson, stateId, actionId, parentEdge, isTemplate) {
    const state = this._getStateFromConversationJson(botJson, stateId)
    const action = this._getActionFromConversationJson(botJson, stateId, actionId)

    const node = {
      type: NODE_TYPES.OUTPUT,
      isTemplate: isTemplate,
      parent: parentEdge,
      children: [],
      actionId: actionId,
      stateId: stateId
    }

    if(action.content.type === 'structure') {
      // TODO structure can be an array
      for(const [index, button] of action.content.payload.buttons.entries()) {
        if(button.type === EDGE_TRANSITION_TYPES.POSTBACK) {
          const suffix = state.type === STATE_TYPES.TEMPLATE_REF ? state.postbackSuffix : ''
          const postbackKey = button.payload + suffix
          const transition = {
            type: EDGE_TRANSITION_TYPES.POSTBACK,
            title: button.title,
            postback: postbackKey,
            index: index
          }
          const postbackGenerator = new StateTreeGenerator({
            postbackMode: true, conditionMode: this.conditionMode, templates: this.templates, conditionNode: this.conditionNode
          })
          const postbackNode = postbackGenerator._processState(botJson, botJson.postbacks[postbackKey], this._createEdge(node, transition), isTemplate)
          node.children.push(this._createEdge(postbackNode, transition))
        }
      }
    }

    return node
  }

  _processRemoteAction(stateId, actionId, parentEdge, isTemplate) {
    const node = {
      type: NODE_TYPES.REMOTE,
      isTemplate: isTemplate,
      parent: parentEdge,
      children: [],
      actionId: actionId,
      stateId: stateId
    }

    return node
  }

  _processChatGPTAction(stateId, actionId, parentEdge, isTemplate) {
    return {
      type: NODE_TYPES.CHAT_GPT,
      isTemplate: isTemplate,
      parent: parentEdge,
      children: [],
      actionId: actionId,
      stateId: stateId
    }
  }

  _processValidationAction(stateId, actionId, parentEdge, isTemplate) {
    const node = {
      type: NODE_TYPES.VALIDATION,
      isTemplate,
      parent: parentEdge,
      children: [],
      actionId,
      stateId
    }

    return node
  }

  _processTransitions(node, transitions, botJson, stateId, isTemplate) {
    const edges = []

    if(isEmpty(transitions)) {
      const edgeTransition = { type: EDGE_TRANSITION_TYPES.NONE }
      const continueNode = this._createContinueTemplateNode(this._createEdge(node, edgeTransition), this.postbackMode)
      edges.push(this._createEdge(continueNode, edgeTransition))
    }

    // PRO-367: the aiJudge transition self-loops into its own state, so it gets no edge of its own
    // (_createTransition returns null for it, which also keeps this walk from recursing forever).
    // Its presence only re-labels the findIntent fallthrough as the Continue-or-FindAnswer ending.
    const hasAiJudge = transitions.some(transition => transition.condition?.input === INPUT_TYPES.AI_JUDGE)

    for(const transition of transitions) {
      const edgeTransition = this._createTransition(transition, botJson, stateId)
      if(!edgeTransition) continue

      const childNode = this._processState(botJson, transition.state, this._createEdge(node, edgeTransition), isTemplate, transition.condition.input)
      if(hasAiJudge && childNode.type === NODE_TYPES.FIND_INTENT) childNode.type = NODE_TYPES.CONTINUE_OR_FIND_INTENT
      edges.push(this._createEdge(childNode, edgeTransition))
    }

    return edges
  }

  _createTransition(transition, botJson, stateId) {
    const inputType = transition.condition.input
    if (inputType === INPUT_TYPES.NONE) {
      return {
        type: EDGE_TRANSITION_TYPES.NONE
      }
    } else if(inputType === INPUT_TYPES.USER_INPUT) {
      return {
        type: EDGE_TRANSITION_TYPES.USER_INPUT
      }
    } else if(inputType === INPUT_TYPES.QUICK_REPLY) {
      const actionId = last(this._getStateActions(botJson, stateId))
      const qrAction = this._getActionFromConversationJson(botJson, stateId, actionId)

      if (qrAction && qrAction.content.quick_replies) {
        const qrIndex = findIndex(propEq('payload', transition.condition.expression.equals), qrAction.content.quick_replies)
        const qrButton = qrAction.content.quick_replies[qrIndex]
        return {
          type: EDGE_TRANSITION_TYPES.QUICK_REPLY,
          title: qrButton.title,
          index: qrIndex,
          payload: qrButton.payload
        }
      }
    }

    return null
  }

  _getStateActions(botJson, stateId) {
    const state = this._getStateFromConversationJson(botJson, stateId)
    if(state.type === STATE_TYPES.TEMPLATE_REF) {
      return this._getTemplateJson(state.fromTemplate).states[state.templateState].actions
    }

    return state.actions
  }

  _getTemplateJson(fromTemplateStateId) {
    const templateId = fromTemplateStateId.replace('ref:template:', '')
    return this.templates[templateId]
  }

  _getStateFromConversationJson(botJson, stateId, inputType) {
    switch (stateId) {
      case STATE_ID.REF_GLOBAL_FIND_INTENT:
        return { type: STATE_TYPES.REMOTE }
      case STATE_ID.REF_GLOBAL_IDLE:
        return { type: STATE_TYPES.CALL_OPERATOR }
      default:
        return botJson.states[stateId] || null
    }
  }

  _getActionFromConversationJson(botJson, stateId, actionId) {
    if (botJson.actions[actionId]) {
      return botJson.actions[actionId]
    }

    const templateId = botJson.states[stateId].fromTemplate.replace('ref:template:', '')
    const templateJson = this._getTemplateJson(templateId)
    return templateJson.actions[actionId]
  }

  _createEmptyNode(stateId, parentEdge, isTemplate) {
    if ([EDGE_TRANSITION_TYPES.QUICK_REPLY, EDGE_TRANSITION_TYPES.POSTBACK].includes(parentEdge?.transition.type)) {
      return this._createIdleNode(parentEdge, isTemplate, false, null, this.postbackMode, !!parentEdge.transition.payload)
    }

    return {
      type: NODE_TYPES.EMPTY,
      parent: parentEdge,
      stateId: stateId
    }
  }

  _createContinueTemplateNode(parentEdge, isPostback) {
    return {
      type: NODE_TYPES.CONTINUE_TEMPLATE,
      parent: parentEdge,
      isPostback: !!isPostback
    }
  }

  _createContinueConditionNode(parentEdge, conditionNode, isTemplate) {
    return {
      type: NODE_TYPES.CONTINUE_CONDITION,
      parent: parentEdge,
      isTemplate: isTemplate,
      isCondition: true,
      conditionNode: conditionNode
    }
  }

  _createContinueAnswerNode(answerId, parentEdge, isTemplate, isCondition, conditionNode, isPostback, isQuickReply) {
    return {
      type: NODE_TYPES.CONTINUE_ANSWER,
      parent: parentEdge,
      isTemplate: isTemplate,
      isCondition: !!isCondition,
      conditionNode: conditionNode,
      isPostback: !!isPostback,
      isQuickReply: !!isQuickReply,
      answerId: answerId
    }
  }

  _createFindIntentNode(parentEdge, isTemplate, isCondition, conditionNode, isPostback, isQuickReply) {
    return {
      type: NODE_TYPES.FIND_INTENT,
      parent: parentEdge,
      isTemplate: isTemplate,
      isCondition: !!isCondition,
      conditionNode: conditionNode,
      isPostback: !!isPostback,
      isQuickReply: !!isQuickReply
    }
  }

  _createIdleNode(parentEdge, isTemplate, isCondition, conditionNode, isPostback, isQuickReply) {
    return {
      type: NODE_TYPES.IDLE,
      parent: parentEdge,
      isTemplate: isTemplate,
      isCondition: !!isCondition,
      conditionNode: conditionNode,
      isPostback: !!isPostback,
      isQuickReply: !!isQuickReply,
      stateId: (() => {
        if (isPostback) return parentEdge?.transition.postback
        if (isQuickReply) return parentEdge?.transition.payload
        return parentEdge?.node.stateId
      })()
    }
  }

  _createEdge(node, transition) {
    return {
      node,
      transition
    }
  }

}

export { StateTreeGenerator as default, NODE_TYPES, EDGE_TRANSITION_TYPES }
